import React from 'react';

export interface LanguageChoiceOption {
  code: string;
  label: string;
}

/**
 * Language picker as an inline segmented control, for use *inside* another menu.
 *
 * <p>
 * Same reasoning as {@link ThemeChoice}: nesting the dropdown version inside the user menu breaks
 * on touch. With two languages this is also simply better — both are visible, and switching is one
 * tap rather than three.
 * </p>
 *
 * <p>
 * It shows full names rather than the two-letter codes the old public-site pill used. Inside a menu
 * there is room, and "Română" is unambiguous in a way "RO" is not to someone who does not already
 * know which languages are on offer.
 * </p>
 */
export const LanguageChoice: React.FC<{
  value: string;
  languages: LanguageChoiceOption[];
  onChange: (code: string) => void;
  label?: string;
}> = ({ value, languages, onChange, label }) => {
  const current = languages.find((l) => value.startsWith(l.code)) ?? languages[0];
  return (
    <div className="px-3 py-2">
      <p className="mb-1.5 text-xs font-medium text-subtle-ink">{label ?? 'Language'}</p>
      <div role="group" aria-label={label ?? 'Language'} className="flex rounded-lg border border-line p-0.5">
        {languages.map((l) => (
          <button
            key={l.code}
            type="button"
            aria-pressed={l.code === current?.code}
            onClick={() => onChange(l.code)}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition ${
              l.code === current?.code ? 'bg-brand-600 text-white' : 'text-muted-strong hover:bg-subtle'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
};
